import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, LogIn, LogOut, QrCode } from 'lucide-react';
import { SwapRequest } from '../components/SwapRequest';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Podgorica' });

// Staff entrance QR → /shift-scan?k=… : log in (or reuse the admin session) and the scan starts / ends the shift.
export default function ShiftScan() {
  const [params] = useSearchParams();
  const k = params.get('k') || '';
  const b = params.get('b') || '';
  const [token, setToken] = useState(() => localStorage.getItem('admin_token') || '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [info, setInfo] = useState(null);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const check = async (tok) => {
    setErr('');
    const r = await fetch(`${API}/api/attendance/check?k=${encodeURIComponent(k)}`, { headers: { Authorization: `Bearer ${tok}` } });
    const d = await r.json();
    if (r.status === 401) { setToken(''); localStorage.removeItem('admin_token'); return; }
    if (!r.ok) throw new Error(d.detail || 'Error');
    setInfo(d);
  };
  useEffect(() => { if (token && k) check(token).catch((e) => setErr(e.message)); }, [token, k]); // eslint-disable-line react-hooks/exhaustive-deps
  const badgeScan = async (action) => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/attendance/badge-scan`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ b, action }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Scan failed');
      setResult(d);
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };
  const scannedRef = React.useRef(false);
  useEffect(() => { if (b && !scannedRef.current) { scannedRef.current = true; badgeScan(); } }, [b]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/auth/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username: username.trim(), password }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Login failed');
      localStorage.setItem('admin_token', d.access_token); setToken(d.access_token); setPassword('');
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };
  const scan = async (action) => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/attendance/scan`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ k, action }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Scan failed');
      setResult(d);
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };
  const switchUser = () => { localStorage.removeItem('admin_token'); setToken(''); setInfo(null); setResult(null); };

  let body;
  if (b) body = result ? (
    <div className="text-center space-y-3" data-testid={`shift-scan-done-${result.action}`}>
      <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto" />
      <p className="text-2xl font-bold text-white">{result.action === 'start' ? 'Shift started' : 'Shift ended'}</p>
      <p className="text-white/80"><b>{result.username}</b> · {result.action === 'start' ? `since ${fmt(result.shift.start_at)}` : `${fmt(result.shift.start_at)} – ${fmt(result.shift.end_at)} · ${result.shift.hours} h`}</p>
      <p className="text-white/50 text-sm">Management has been notified. Have a good {result.action === 'start' ? 'shift' : 'rest'}!</p>
      <button type="button" onClick={() => { setResult(null); badgeScan(result.action === 'start' ? 'end' : 'start'); }} disabled={busy} className="text-xs underline text-white/60" data-testid="shift-scan-undo">Scanned by mistake? {result.action === 'start' ? 'End the shift again' : 'Re-open the shift'}</button>
      <SwapRequest auth={{ b }} />
    </div>
  ) : (err ? <p className="text-white/60 text-sm">Show this badge to your manager if the problem persists.</p> : <p className="text-white/70 inline-flex items-center gap-2" data-testid="shift-scan-badge-busy"><Loader2 className="w-4 h-4 animate-spin" /> Registering your scan…</p>);
  else if (!k) body = <p className="text-red-200" data-testid="shift-scan-nok">This link is missing the code — please scan the QR at the staff entrance.</p>;
  else if (!token) body = (
    <form onSubmit={login} className="space-y-3" data-testid="shift-scan-login">
      <p className="text-white/70 text-sm">Log in with your staff account to register your shift.</p>
      <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" autoComplete="username" className="w-full rounded-xl bg-white/10 border border-white/20 px-4 py-3 text-white placeholder-white/40" data-testid="shift-scan-username" />
      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" autoComplete="current-password" className="w-full rounded-xl bg-white/10 border border-white/20 px-4 py-3 text-white placeholder-white/40" data-testid="shift-scan-password" />
      <button type="submit" disabled={busy || !username || !password} className="w-full rounded-xl bg-orange-500 hover:bg-orange-400 text-white font-bold py-3 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="shift-scan-login-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />} Log in</button>
    </form>
  );
  else if (result) body = (
    <div className="text-center space-y-3" data-testid={`shift-scan-done-${result.action}`}>
      <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto" />
      <p className="text-2xl font-bold text-white">{result.action === 'start' ? 'Shift started' : 'Shift ended'}</p>
      <p className="text-white/80">{result.shift.username} · {result.action === 'start' ? `since ${fmt(result.shift.start_at)}` : `${fmt(result.shift.start_at)} – ${fmt(result.shift.end_at)} · ${result.shift.hours} h`}</p>
      <p className="text-white/50 text-sm">Management has been notified. Have a good {result.action === 'start' ? 'shift' : 'rest'}!</p>
      <button type="button" onClick={switchUser} className="text-xs underline text-white/60" data-testid="shift-scan-switch">Not you? Switch account</button>
      <SwapRequest auth={{ token }} />
    </div>
  );
  else if (info) body = (
    <div className="space-y-4" data-testid="shift-scan-ready">
      <p className="text-white text-lg">Hello <b>{info.username}</b> <span className="text-white/50 text-sm">({(info.role || '').replace('_', ' ')})</span></p>
      {info.open_shift && <p className="text-white/70 text-sm" data-testid="shift-scan-open">Your shift is open since {fmt(info.open_shift.start_at)}{info.next === 'start' ? ' — that looks forgotten, it will be closed and a new one started.' : '.'}</p>}
      <button type="button" onClick={() => scan(info.next)} disabled={busy} className={`w-full rounded-2xl py-5 text-xl font-bold text-white inline-flex items-center justify-center gap-3 disabled:opacity-50 ${info.next === 'start' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-rose-600 hover:bg-rose-500'}`} data-testid={`shift-scan-${info.next}`}>
        {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : (info.next === 'start' ? <LogIn className="w-6 h-6" /> : <LogOut className="w-6 h-6" />)} {info.next === 'start' ? 'Start my shift' : 'End my shift'}
      </button>
      <button type="button" onClick={switchUser} className="w-full text-xs underline text-white/60" data-testid="shift-scan-switch">Not {info.username}? Switch account</button>
      <SwapRequest auth={{ token }} />
    </div>
  );
  else body = <p className="text-white/70 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Checking…</p>;

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="shift-scan-page">
      <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md p-6 shadow-2xl">
        <p className="text-orange-400 text-xs font-semibold tracking-widest uppercase inline-flex items-center gap-2 mb-4"><QrCode className="w-4 h-4" /> La Rosa Negra · Staff shift</p>
        {err && <p className="text-sm text-red-300 mb-3" data-testid="shift-scan-error">{err}</p>}
        {body}
      </div>
    </div>
  );
}
